import type { User } from '@printtat/shared'

import { useState } from 'react'
import { useForm } from '@tanstack/react-form'
import { z } from 'zod'
import { FormField, Button, Alert } from '@printtat/ui'
import { apiFetch } from '@/lib/apiFetch'

// Zod schema for password setup
const AccountPasswordFormSchema = z.object({
  password: z.string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[0-9]/, "Password must contain at least one number")
    .regex(/[^A-Za-z0-9]/, "Password must contain at least one special character"),
  confirm_password: z.string(),
}).refine((data) => data.password === data.confirm_password, {
  message: "Passwords don't match",
  path: ["confirm_password"],
})

// Zod schema for password change
const passwordChangeSchema = z.object({
  current_password: z.string().min(1, "Current password is required"),
  new_password: z.string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[0-9]/, "Password must contain at least one number")
    .regex(/[^A-Za-z0-9]/, "Password must contain at least one special character"),
  confirm_new_password: z.string(),
}).refine((data) => data.new_password === data.confirm_new_password, {
  message: "Passwords don't match",
  path: ["confirm_new_password"],
})

interface AccountPasswordFormProps {
  user: User
}

export const AccountPasswordForm = ({ user }: AccountPasswordFormProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const hasPassword = !!user.password
  const isGoogleOnly = !!user.googleId && !user.password

  const setupForm = useForm({
    defaultValues: {
      password: '',
      confirm_password: '',
    },
    onSubmit: async ({ value }) => {
      const validationResult = AccountPasswordFormSchema.safeParse(value)
      if (!validationResult.success) {
        setError(validationResult.error.issues[0]?.message || 'Validation failed')
        return
      }

      setIsSubmitting(true)
      setError(null)
      setSuccess(false)

      try {
        const response = await apiFetch('/auth/setup-password', {
          method: 'POST',
          body: { password: value.password },
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.message || 'Failed to setup password')
        }

        setSuccess(true)
        setIsEditing(false)
        window.location.reload()
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred')
      } finally {
        setIsSubmitting(false)
      }
    },
  })

  const changeForm = useForm({
    defaultValues: {
      current_password: '',
      new_password: '',
      confirm_new_password: '',
    },
    onSubmit: async ({ value }) => {
      const validationResult = passwordChangeSchema.safeParse(value)
      if (!validationResult.success) {
        setError(validationResult.error.issues[0]?.message || 'Validation failed')
        return
      }

      setIsSubmitting(true)
      setError(null)
      setSuccess(false)

      try {
        const response = await apiFetch('/auth/change-password', {
          method: 'POST',
          body: {
            currentPassword: value.current_password,
            newPassword: value.new_password,
          },
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.message || 'Failed to change password')
        }

        setSuccess(true)
        setIsEditing(false)
        window.location.reload()
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred')
      } finally {
        setIsSubmitting(false)
      }
    },
  })

  if (!isEditing) {
    return (
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {hasPassword
            ? 'Update your password to keep your account secure.'
            : isGoogleOnly
              ? 'Set up a password to enable email/password sign-in alongside Google.'
              : 'Set up a password for your account.'
          }
        </p>
        <Button
          variant="outline"
          size="sm"
          color="dark"
          onClick={() => setIsEditing(true)}
        >
          {hasPassword ? 'Change Password' : 'Set Up Password'}
        </Button>
      </div>
    )
  }

  // Password setup form (no existing password)
  if (!hasPassword) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setupForm.handleSubmit()
        }}
        className="space-y-4"
      >
        {isGoogleOnly && (
          <p className="text-sm text-gray-600">
            This will allow you to sign in with either your Google account or email/password.
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            form={setupForm}
            name="password"
            label="New Password"
            type="password"
            placeholder="Enter your new password"
            required
          />

          <FormField
            form={setupForm}
            name="confirm_password"
            label="Confirm Password"
            type="password"
            placeholder="Confirm your new password"
            required
          />
        </div>

        {error && <Alert variant="error">{error}</Alert>}
        {success && <Alert variant="success">Password set up successfully!</Alert>}

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setIsEditing(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            color="dark"
            disabled={!setupForm.state.canSubmit}
            loading={isSubmitting}
          >
            Set Up Password
          </Button>
        </div>
      </form>
    )
  }

  // Password change form (has existing password)
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        changeForm.handleSubmit()
      }}
      className="space-y-4"
    >
      <FormField
        form={changeForm}
        name="current_password"
        label="Current Password"
        type="password"
        placeholder="Enter your current password"
        required
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          form={changeForm}
          name="new_password"
          label="New Password"
          type="password"
          placeholder="Enter your new password"
          required
        />

        <FormField
          form={changeForm}
          name="confirm_new_password"
          label="Confirm New Password"
          type="password"
          placeholder="Confirm your new password"
          required
        />
      </div>

      {error && <Alert variant="error">{error}</Alert>}
      {success && <Alert variant="success">Password changed successfully!</Alert>}

      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setIsEditing(false)}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          color="dark"
          disabled={!changeForm.state.canSubmit}
          loading={isSubmitting}
        >
          Change Password
        </Button>
      </div>
    </form>
  )
}
